import { db } from '../../../firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  DocumentReference,
  DocumentData,
  collection,
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { HouseholdMember, Role } from '../../../../src/types';
import { Household } from '@/types';

/**
 * 🛰️ HYBRID SCOPING ARCHITECTURE
 * * Personal Scope: /users/{uid}/{collection}
 * Shared Scope:   /households/{hid}/{collection}
 * * This service manages the bridge between these partitions.
 */

/**
 * Initializes a new household and associates it with the current user.
 * Personal data remains in /users/{uid}, while this creates the shared hub.
 */
export async function initializeHousehold(
  user: User,
  householdName: string
): Promise<Household> {
  if (!user || !user.uid) {
    console.warn("initializeHousehold: User not authenticated."); // Added logging
    throw new Error('User not authenticated.');
  }

  const householdId = doc(collection(db, 'households')).id;
  console.log("initializeHousehold: Generated new householdId:", householdId); // Added logging

  const ownerMember: HouseholdMember = {
    id: user.uid,
    userId: user.uid,
    name: user.displayName || user.email || 'New Member',
    email: user.email || '',
    role: Role.OWNER,
    joinedAt: new Date().toISOString(),
  };

  const newHousehold: Household = {
    id: householdId,
    name: householdName,
    ownerId: user.uid,
    memberIds: [user.uid],
    memberLimit: 6,
    type: 'FAMILY',
    members: [ownerMember],
    permissions: {
      [user.uid]: {
        canManageTasks: true,
        canDeleteTasks: true,
        canManageEvents: true,
        canManageGroceries: true,
        canDeleteGroceries: true,
        canManageInventory: true,
        canManageContacts: true,
        canViewAuditLogs: true,
        role: Role.OWNER,
      },
    },
    address: null,
    activeInvite: undefined,
  };
  console.log("initializeHousehold: Constructed newHousehold object:", newHousehold);

  try {
    const householdRef = doc(db, 'households', householdId);
    console.log("initializeHousehold: Attempting to setDoc for householdRef:", householdRef.path); // Added logging
    await setDoc(householdRef, newHousehold);
    console.log("initializeHousehold: Successfully created household document."); // Added logging

    const userRef: DocumentReference<DocumentData> = doc(db, 'users', user.uid);
    console.log("initializeHousehold: Attempting to getDoc for userRef:", userRef.path); // Added logging
    const userDoc = await getDoc(userRef);
    console.log("initializeHousehold: getDoc for userRef completed. UserDoc exists:", userDoc.exists()); // Added logging

    if (userDoc.exists()) {
      console.log("initializeHousehold: Updating existing user profile."); // Added logging
      await updateDoc(userRef, {
        memberOf: arrayUnion(householdId),
        currentHouseholdId: householdId,
        updatedAt: new Date().toISOString()
      });
      console.log("initializeHousehold: Successfully updated user profile."); // Added logging
    } else {
      console.log("initializeHousehold: Creating new user profile (if not already created by App.tsx)."); // Added logging
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        memberOf: [householdId],
        currentHouseholdId: householdId,
        createdAt: new Date().toISOString()
      });
      console.log("initializeHousehold: Successfully created/updated user profile."); // Added logging
    }

    return newHousehold;
  } catch (error: any) { // Explicitly type error as any for .code access if needed
    console.error('Error initializing household in service (re-throwing):', error.message, error.code, error); // More detailed error logging
    throw new Error(`Failed to initialize household: ${error.message || 'Unknown error'}`); // Re-throw with more detail
  }
}

/**
 * Joins a household. Adheres to the household's permission matrix.
 */
export async function joinHouseholdWithCode(user: User, inviteCode: string): Promise<Household> {
  if (!user || !user.uid) {
    throw new Error('User not authenticated.');
  }
  if (!inviteCode || inviteCode.trim() === '') {
    throw new Error('Invite code cannot be empty.');
  }

  const cleanCode = inviteCode.toUpperCase().trim();
  const inviteRef = doc(db, 'invites', cleanCode);

  try {
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists()) {
      throw new Error('Invalid or non-existent invite code.');
    }

    const inviteData = inviteSnap.data() as { householdId: string; expiresAt: string; };

    if (Date.now() > new Date(inviteData.expiresAt).getTime()) {
      throw new Error('This invite has expired.');
    }

    const householdRef = doc(db, 'households', inviteData.householdId);
    const householdSnap = await getDoc(householdRef);

    if (!householdSnap.exists()) {
      throw new Error('Target household does not exist.');
    }

    const household = { ...householdSnap.data(), id: householdSnap.id } as Household;

    if (household.memberIds.includes(user.uid)) {
      throw new Error('You are already a member of this household.');
    }

    if (household.memberIds.length >= (household.memberLimit || 6)) {
      throw new Error('This household has reached its member limit.');
    }

    const newMemberProfile: HouseholdMember = {
      id: user.uid,
      userId: user.uid,
      name: user.displayName || user.email || 'New Member',
      email: user.email || '',
      role: Role.GUEST, 
      joinedAt: new Date().toISOString(),
    };

    // 🔒 Update Shared Scope
    await updateDoc(householdRef, {
      memberIds: arrayUnion(user.uid),
      members: arrayUnion(newMemberProfile),
    });

    // 🔒 Update Personal Scope Metadata
    const userRef: DocumentReference<DocumentData> = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      await updateDoc(userRef, {
        memberOf: arrayUnion(inviteData.householdId),
        currentHouseholdId: inviteData.householdId,
      });
    } else {
      await setDoc(userRef, {
        uid: user.uid,
        memberOf: [inviteData.householdId],
        currentHouseholdId: inviteData.householdId,
      });
    }

    const updatedHouseholdSnap = await getDoc(householdRef);
    return { ...updatedHouseholdSnap.data(), id: updatedHouseholdSnap.id } as Household;

  } catch (error) {
    console.error('Error joining household by code:', error);
    throw error;
  }
}

/**
 * ⚖️ SCOPE MANAGEMENT: updateMemberLimit
 * Restricted to Shared Scope owners.
 */
export async function updateMemberLimit(
  householdId: string,
  newLimit: number,
  currentUserId: string
): Promise<void> {
  if (newLimit <= 0) {
    throw new Error('Member limit must be a positive number.');
  }

  const householdRef = doc(db, 'households', householdId);

  try {
    const householdDoc = await getDoc(householdRef);

    if (!householdDoc.exists()) {
      throw new Error('Household not found.');
    }

    const householdData = householdDoc.data() as Household;

    // Authorization check against the household's permission matrix/owner status
    if (householdData.ownerId !== currentUserId) {
      throw new Error('Unauthorized: Only the household owner can change the member limit.');
    }

    await updateDoc(householdRef, {
      memberLimit: newLimit,
    });
  } catch (error) {
    console.error('Error updating member limit:', error);
    throw new Error('Failed to update member limit.');
  }
}